import { createMuiTheme, fade } from "@material-ui/core/styles";
import { AauxNextFonts } from "./fonts";

export const LARGE_BUTTON = {
  padding: "12px 24px 13px 24px",
  fontSize: "16px",
  lineHeight: "19px",
  letterSpacing: "0.5px",
};

export const PRIMARY_BLUE = {
  "900": "#002373",
  "800": "#123488",
  "700": "#1c3f94",
  "600": "#2648a0",
  "500": "#2d4fa9",
  "400": "#5069b5",
  "300": "#7084c1",
  "200": "#99a6d3",
  "100": "#c1c9e5",
  "50": "#E7E9F4",
};

const SECONDARY_BLUE = {
  "900": "#005a97",
  "800": "#007ab9",
  "700": "#008bcc",
  "600": "#059ee0",
  "500": "#0cacef",
  "400": "#2fb9f1",
  "300": "#53c5f2",
  "200": "#83d6f6",
  "100": "#b4e6f9",
  "50": "#e1f5fd",
};

const BORDER_COLOR = "rgba(33, 33, 33, 0.08);";

const grey = {
  "900": "#313434",
  "800": "#43494b",
  "700": "#515C5F",
  "600": "#607074",
  "500": "#6c8085",
  "400": "#849296",
  "300": "#9ca5a8",
  "200": "#bcbebf",
  "100": "#d8d8d8",
  "50": "#efefef",
};

const text = {
  primary: grey["900"],
  secondary: fade(grey["900"], 0.7),
  disabled: fade(grey["900"], 0.6),
  hint: fade(grey["900"], 0.8),
};

// colors defined in Figma, but not used yet
const invertedText = {
  primary: "#FFF",
  secondary: fade("#FFF", 0.7),
  disabled: fade("#FFF", 0.5),
  hint: fade("#FFF", 0.7),
};

const primary = {
  main: PRIMARY_BLUE["700"],
  light: PRIMARY_BLUE["300"],
  dark: PRIMARY_BLUE["900"],
  contrastText: invertedText.primary,
};

const secondary = {
  main: SECONDARY_BLUE["600"],
  light: SECONDARY_BLUE["200"],
  dark: SECONDARY_BLUE["800"],
  contrastText: invertedText.primary,
};

const error = {
  main: "#b22e2a",
  light: "#ea6053",
  dark: "#7b0000",
  contrastText: invertedText.primary,
};

const warning = {
  main: "#f15a31",
  light: "#ff8c5d",
  dark: "#b72502",
  contrastText: text.primary,
};

const success = {
  main: "#34a758",
  light: "#6bd986",
  dark: "#00772d",
  contrastText: invertedText.primary,
};

const info = secondary;
export const theme = createMuiTheme({
  palette: {
    text,
    primary,
    secondary,
    error,
    warning,
    success,
    info,
    divider: fade(grey["900"], 0.12),
    background: {
      paper: "#FFF",
      default: "#F5F6F8",
    },
    grey,
  },
  typography: {
    fontFamily: "AauxNext, 'Helvetica Neue', Helvetica, Arial, sans-serif",
    button: {
      textTransform: "none",
    },
    body1: {
      lineHeight: "26px",
      fontWeight: 500,
      fontSize: "16px",
      letterSpacing: "0.2px",
    },
    body2: {
      fontWeight: 500,
      lineHeight: "20px",
      fontSize: "14px",
      letterSpacing: "0.25px",
    },
    subtitle1: {
      fontWeight: 500,
      lineHeight: "22px",
      fontSize: "17px",
    },
    h6: {
      fontSize: "22px",
      lineHeight: "28px",
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
      lineHeight: "28px",
      fontSize: "24px",
    },
    h4: {
      fontSize: 30,
      lineHeight: 1.2,
    },
    caption: {
      fontSize: "12px",
      fontWeight: 500,
      lineHeight: "18px",
    },
    overline: {
      fontWeight: "bold",
      fontSize: "14px",
      lineHeight: "17px",
    },
  },
  overrides: {
    MuiCssBaseline: {
      "@global": {
        "@font-face": AauxNextFonts,
      },
    },
    MuiAppBar: {
      colorPrimary: {
        color: primary.main,
        backgroundColor: "#FFF",
      },
    },
    MuiTableCell: {
      root: {
        padding: "12px",
      },
      head: {
        fontWeight: 600,
      },
    },
    MuiToolbar: {
      regular: {
        height: 70,
        borderBottom: `1px solid ${BORDER_COLOR}`,
      },
    },
    MuiDrawer: {
      root: {
        width: 70,
      },
      paperAnchorDockedLeft: {
        width: 70,
        // TODO: Create a custom theme's shadows set so we can use the proper elevation props on the Drawer
        boxShadow:
          "0 4px 7.5px rgba(0,0,0,0.1), 0 3px 21px rgba(0,0,0, 0.06), 0 8px 15px rgba(0,0,0, 0.07)",
      },
    },
    MuiDialogTitle: {
      root: {
        padding: "16px 32px",
      },
    },
    MuiDialogContent: {
      root: {
        padding: "24px 32px",
      },
      dividers: {
        padding: "24px 32px",
      },
    },
    // 18 * 2 + 44(button height) = 80 (action height)
    MuiDialogActions: {
      root: {
        padding: "18px 32px",
      },
    },
    MuiButton: {
      contained: {
        boxShadow: "none",
      },
      outlinedPrimary: {
        border: `1px solid ${primary.main}`,
      },
      outlinedSecondary: {
        border: `1px solid ${secondary.main}`,
      },
      outlinedSizeLarge: LARGE_BUTTON,
      textSizeLarge: LARGE_BUTTON,
      containedSizeLarge: LARGE_BUTTON,
    },
    // Input Styles
    MuiInputLabel: {
      root: {
        fontWeight: 500,
      },
    },
    MuiInputBase: {
      root: {
        fontWeight: 500,
        fontSize: 17,
        lineHeight: "22px",
      },
    },
    // @ts-ignore
    MuiAlert: {
      icon: {
        padding: "0px",
        marginRight: 16,
      },
      action: {
        marginRight: 8,
      },
      outlinedWarning: {
        color: text.primary,
      },
    },
    MuiAlertTitle: {
      root: {
        fontWeight: 700,
        marginBottom: 0,
      },
    },
    MuiPagination: {
      root: {
        marginLeft: "1px",
      },
      ul: {
        "& li:first-child button": {
          borderTopLeftRadius: "5px",
          borderBottomLeftRadius: "5px",
          width: "50px",
        },
        "& li:last-child button": {
          borderTopRightRadius: "5px",
          borderBottomRightRadius: "5px",
          width: "50px",
        },
      },
    },
    MuiPaginationItem: {
      root: {
        marginLeft: "-1px",
        marginRight: "0",
        fontWeight: "700",
        lineHeight: "22px",
        color: primary.main,
      },
      page: {
        "&:hover, &$focusVisible": {
          backgroundColor: fade(primary.main, 0.04),
        },
      },
      outlined: {
        borderColor: `${primary.main} !important`,
      },
      sizeLarge: {
        borderRadius: "0",
        height: "48px",
        width: "46px",
        fontSize: "17px",
      },
    },
    MuiListItemText: {
      multiline: {
        marginTop: 4,
        marginBottom: 4,
      },
    },
    MuiPickersDay: {
      dayDisabled: {
        color: "rgba(49, 52, 52, 0.45)",
      },
      current: {
        color: text.primary,
        border: "1px solid",
        borderColor: secondary.main,
      },
      daySelected: {
        border: "0 none",
      },
    },
    MuiTabs: {
      indicator: {
        height: 4,
        backgroundColor: primary.main,
      },
    },
    MuiChip: {
      outlined: {
        fontWeight: "bold",
        lineHeight: 1,
        borderColor: text.secondary,
        borderWidth: "2px",
      },
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: fade(primary.main, 0.08),
        },
      },
    },
    MuiMenuItem: {
      root: {
        "&:hover, &:focus": {
          backgroundColor: fade(primary.main, 0.04),
        },
      },
    },
    MuiListItem: {
      root: {
        "&$selected, &$selected:hover, &$selected:focus": {
          backgroundColor: fade(primary.main, 0.08),
        },
      },
    },
  },
});
